import React, { useState } from 'react';
import { connect } from 'react-redux';
import { ColumnApi, GridApi, ICellRendererParams } from 'ag-grid-enterprise';
import { AgGridReact } from 'ag-grid-react';
import MailToCellRenderer from '$/components/global/mailToCellRenderer';
import NoDataFound from '$/components/global/noData';
import * as Bixby from '$/core';
import { AnalyticsService } from '$/services';
import { AppState } from '$/store';
import {
  getIsCurrentUserClientState,
  getIsCurrentUserCreditSightsAnalystState,
  getIsCurrentUserCreditSightsOpsState,
  getIsCurrentUserClientComplianceAdminState,
  getIsCurrentUserSuFAnalystState,
  getIsCurrentUserRatingsAnalystState,
} from '$/store/profile/selectors';
import { getIsCurrentUserAdminState } from '$/store/profile/selectors';
import Modal from '$/components/global/modal';
import DataSitesLegend from '$/components/global/dataSitesLegend/index';

import './index.scss';

interface INonMnpiAccessProps {
  dataSites: Bixby.Models.ClientDataSiteForClient[] | undefined;
  isAdmin: boolean;
  isClient: boolean;
  isCreditSightsOpsSufOrRatings: boolean;
  profile?: Bixby.Models.Profile;
  isClientComplianceAdmin: boolean;
}

const NonMnpiDataSitesAccess: React.FC<INonMnpiAccessProps> = props => {
  const { isAdmin, isClient, isCreditSightsOpsSufOrRatings, profile, isClientComplianceAdmin, dataSites } = props;

  const [, setGridApi] = useState(null as GridApi | null);
  const [, setGridColumnApi] = useState(null as ColumnApi | null);
  const [modalActive, setModalActive] = useState(false);

  const handleOpenModal = () => {
    setModalActive(true);
  };

  const handleCloseModal = () => {
    setModalActive(false);
  };

  const asteriskNoteMatch = /\w+[*]/;

  const createFeedbackUrl = (dataSites: Bixby.Models.ClientDataSiteForClient) => {
    const feedbackUrlSubject: string = encodeURIComponent(`Company Request: ${dataSites.dataSiteName}`);

    const user: string =
      profile && profile.email && profile.firstName && profile.lastName
        ? `${profile.firstName} ${profile.lastName} (${profile.email})`
        : 'User';
    let newsFeedbackUrlBody: string;
    if (dataSites.notes === 'Eligible') {
      newsFeedbackUrlBody = encodeURIComponent(
        `${user} would like to add ${dataSites.dataSiteName} to ${dataSites.clientName}'s priority list.`
      );
    } else if (dataSites.notes === 'No USD Loans*') {
      newsFeedbackUrlBody = encodeURIComponent(
        `${user} would like to learn more about Bixby's European coverage add-on package and see the following name on a trial basis: ${dataSites.dataSiteName}.`
      );
    } else if (dataSites.notes === 'Data Site Missing Information' || dataSites.notes === 'Other - contact Bixby') {
      newsFeedbackUrlBody = encodeURIComponent(
        `${user} would like to inquire about the status of ${dataSites.dataSiteName}.`
      );
    } else if (dataSites.notes.match(asteriskNoteMatch)) {
      newsFeedbackUrlBody = encodeURIComponent(
        `${user} would like to inquire about ${dataSites.dataSiteName}. This site doesn’t appear to qualify for Bixby coverage. If you believe this was tagged in error please let us know and a member of the team will respond as soon as possible.`
      );
    } else {
      newsFeedbackUrlBody = encodeURIComponent(`${user} would like to request access to: ${dataSites.dataSiteName}.`);
    }
    return `mailto:feedback@bixbyresearch.com?subject=${feedbackUrlSubject}&body=${newsFeedbackUrlBody}`;
  };

  const createContactLinkText = (dataSites: Bixby.Models.ClientDataSiteForClient) => {
    if (dataSites.notes === 'Eligible') {
      return 'Prioritize';
    } else if (dataSites.notes === 'No USD Loans*') {
      return '*NEW* Bixby Euro Coverage';
    } else if (
      dataSites.notes === 'Data Site Missing Information' ||
      dataSites.notes === 'Other - contact Bixby' ||
      dataSites.notes.match(asteriskNoteMatch)
    ) {
      return 'Contact Bixby';
    } else {
      return 'Request Access';
    }
  };

  const handleFeedbackClick = (row: Bixby.Models.ClientDataSiteForClient) => {
    if (profile) {
      AnalyticsService.event(Bixby.Constants.APPINSIGHTS_FEEDBACK_LINK_CLICK, {
        ...profile,
        type: 'DataSites',
        dataSiteName: row.dataSiteName,
      });
    }
  };

  let clientDataSitesViewHeader: string | null = null;
  if (dataSites && !Bixby.Utils.isNullOrEmpty(dataSites)) {
    const clientDataSiteUploadDate: Date = dataSites[0].uploadDate;
    const uploadDate: string = new Date(clientDataSiteUploadDate).toLocaleDateString();
    clientDataSitesViewHeader = `Data Sites: ${dataSites.length} (last updated ${uploadDate})`;
  }

  const onGridReady = (params: any) => {
    setGridApi(params.api);
    setGridColumnApi(params.columnApi);
  };

  const getContextMenuItems = () => {
    const result = ['copy', 'copyWithHeaders', 'separator', 'export'];
    return result;
  };

  const content = isAdmin ? (
    <React.Fragment>
      <section>
        <div className="data-sites-access-section">
          <h3>Admin User</h3>
        </div>
      </section>
    </React.Fragment>
  ) : isCreditSightsOpsSufOrRatings ? (
    <React.Fragment>
      <section>
        <div className="data-sites-access-section">
          <h3>CreditSights User</h3>
        </div>
      </section>
    </React.Fragment>
  ) : isClient ? (
    <React.Fragment>
      <section>
        <div className="data-sites-access-note">
          <p>{clientDataSitesViewHeader}</p>
        </div>
        <div className="data-sites-access-header">
          <p className="data-sites-access-header-note">
            Note:{' '}
            <a className="legend-link" onClick={handleOpenModal}>
              See Legend
            </a>{' '}
            for status details; data site access and Bixby status updated daily; will not reflect intraday changes.
          </p>
          <p className="data-sites-access-header-note">
            *Not currently covered by Bixby; please reach out for further information.
          </p>
        </div>
        <div className="data-sites-access-section">
          {Bixby.Utils.isNullOrEmpty(dataSites) && <NoDataFound dataType="data sites" />}
          {!Bixby.Utils.isNullOrEmpty(dataSites) && (
            <div className="data-sites-access-data-grid">
              <div className="grid_container">
                <div className="grid ag-theme-alpine">
                  <AgGridReact
                    enableRangeSelection={false}
                    getContextMenuItems={getContextMenuItems}
                    defaultColDef={{
                      minWidth: 100,
                      sortable: true,
                      resizable: true,
                      lockPinned: true,
                    }}
                    onGridReady={onGridReady}
                    rowData={dataSites}
                    suppressDragLeaveHidesColumns={true}
                    suppressMovableColumns={true}
                    suppressCellFocus={true}
                    animateRows={true}
                    rowHeight={32}
                    headerHeight={32}
                    components={{
                      mailToCellRenderer: MailToCellRenderer,
                    }}
                    columnDefs={[
                      {
                        field: 'dataSiteName',
                        filter: 'agTextColumnFilter',
                        floatingFilter: true,
                        headerName: 'Data Site Name',
                        minWidth: 250,
                        pinned: 'left',
                        flex: 1,
                      },
                      {
                        field: 'companyName',
                        filter: 'agTextColumnFilter',
                        floatingFilter: true,
                        headerName: 'Bixby Access (Company)',
                        cellRenderer: (params: any) => {
                          if (params.data['companyId']) {
                            const url = '/company/' + params.data['companyId'];
                            return (
                              <a className="bix-link link_underline" href={url}>
                                {' '}
                                {params.data['companyName']}{' '}
                              </a>
                            );
                          } else {
                            return <div title={params.data['companyName']}> {params.data['companyName']}</div>;
                          }
                        },
                        minWidth: 250,
                        flex: 1,
                      },
                      {
                        headerName: 'Loan Ticker',
                        field: 'openFigiTicker',
                        filter: 'agTextColumnFilter',
                        floatingFilter: true,
                        width: 130,
                      },
                      {
                        field: 'dataSiteEmail',
                        filter: 'agTextColumnFilter',
                        headerName: 'Data Site Email',
                        minWidth: 200,
                        hide: !isClientComplianceAdmin,
                      },
                      {
                        field: 'notes',
                        filter: 'agTextColumnFilter',
                        floatingFilter: true,
                        headerName: 'Status',
                        minWidth: 250,
                        flex: 1,
                      },
                      {
                        field: 'allowInquiry',
                        headerName: 'Prioritize / Contact Bixby',
                        cellRenderer: 'mailToCellRenderer',
                        cellRendererParams: {
                          show: (params: ICellRendererParams) => {
                            return params.data['allowInquiry'] && params.data.notes !== 'Available on Bixby';
                          },
                          createFeedbackUrl: (params: Bixby.Models.ClientDataSiteForClient) =>
                            createFeedbackUrl(params),
                          handleFeedbackClick: (params: Bixby.Models.ClientDataSiteForClient) =>
                            handleFeedbackClick(params),
                          createContactLinkText: (params: Bixby.Models.ClientDataSiteForClient) =>
                            createContactLinkText(params),
                        },
                        minWidth: 250,
                        flex: 1,
                      },
                    ]}></AgGridReact>
                </div>
              </div>
            </div>
          )}
        </div>
      </section>
      <Modal title="Data Site Status Legend" active={modalActive} hideClose={true}>
        <DataSitesLegend onCloseModal={handleCloseModal} />
      </Modal>
    </React.Fragment>
  ) : null;

  return <>{content}</>;
};

const mapStateToProps = (state: AppState) => {
  const { isClient } = getIsCurrentUserClientState(state);
  const { isAdmin } = getIsCurrentUserAdminState(state);
  const { isCreditSightsAnalyst } = getIsCurrentUserCreditSightsAnalystState(state);
  const { isSuFAnalyst } = getIsCurrentUserSuFAnalystState(state);
  const { isCreditSightsOps } = getIsCurrentUserCreditSightsOpsState(state);
  const { isClientComplianceAdmin } = getIsCurrentUserClientComplianceAdminState(state);
  const { isRatingsAnalyst } = getIsCurrentUserRatingsAnalystState(state);

  return {
    isAdmin,
    isClient,
    isCreditSightsOpsSufOrRatings: isCreditSightsAnalyst || isCreditSightsOps || isSuFAnalyst || isRatingsAnalyst,
    isClientComplianceAdmin,
    profile: state.profileState.profile,
  };
};

const mapDispatchToProps = () => ({});

export default connect(mapStateToProps, mapDispatchToProps)(NonMnpiDataSitesAccess);
